
import { Flex } from '@momentum-ui/react-collaboration';
import React, { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// Infinite scroll: expects fetchNextBatch and loading from parent/hook
import { VariableSizeList as VirtualizedList, ListChildComponentProps } from 'react-window';
import useWebexClasses from '../../hooks/useWebexClasses';
import { VoicemailItem, IVoicemailItemProps} from './VoicemailItem';
import { VoicemailAudioProvider } from './VoicemailAudioProvider';
import './VoicemailItem.styles.scss';


// Define the props for the voicemail list
export interface IVoicemailListProps {
  fetchNextBatch?: () => Promise<unknown> | void;
  items: IVoicemailItemProps[]; // Replace 'any' with your actual voicemail item type
  height?: number;
  width?: number | string;
  itemHeight?: number;
  style?: React.CSSProperties;
  onItemClick?: (item: IVoicemailItemProps) => void;
  isActive?: boolean; // Indicates if the tab is currently visible/active
  testMode?: boolean;
}

// Stable row renderer placed outside main component scope to avoid identity changes each render
// It receives data via react-window's itemData prop (kept stable via useMemo in parent)
interface RowDataShape {
  items: IVoicemailItemProps[];
  isFetching: boolean;
  exhausted: boolean;
  hasUserScrolled: boolean;
  measuredHeights: Record<string, number>; // dynamic measured heights for selected rows
  onMeasured: (voicemailId: string, h: number, index: number) => void;
  t: (k: string) => string;
  expandedPadding: number;
}

const VoicemailRow: React.FC<ListChildComponentProps<RowDataShape>> = memo(({ index, style, data }) => {
  const { items, isFetching, exhausted, hasUserScrolled, t, onMeasured } = data;
  const showLoadingIndicator = isFetching && !exhausted && ((items.length === 0) || hasUserScrolled);
  if (showLoadingIndicator && index === items.length) {
    return (
      <div style={style} key="loading-indicator">
        <div style={{ textAlign: 'center', padding: 12, color: '#666' }}>
          {t('loadingMoreVoicemails')}
        </div>
      </div>
    );
  }
  const item = items[index];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Measure selected item (transcript may expand / collapse). Only measure when selected.
  useEffect(() => {
    if (!item?.voicemail?.id) return;
    if (!item.isSelected) return; // only measure expanded/selected row
    const el = contentRef.current; // measure actual content, not outer positioned div
    if (!el || typeof ResizeObserver === 'undefined') return;
    let frame: number | null = null;
    const measure = () => {
      const h = Math.max(el.scrollHeight, el.clientHeight);
      onMeasured(item.voicemail.id, h, index);
    };
    measure(); // initial
    const observer = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(el);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [item.isSelected, item?.voicemail?.id, index, onMeasured]);

  const isLast = index === data.items.length - 1;
  return (
    <div
      style={{
        ...style,
        // If collapsed enforce visual wrapper height to 50
        height: item.isSelected ? style?.height : 50,
        maxHeight: item.isSelected ? style?.height : 50,
        minHeight: item.isSelected ? style?.height : 50,
      }}
      data-virtual-height={style?.height}
      data-collapsed={!item.isSelected}
      key={item.voicemail.id}
      ref={containerRef}
      className="voicemail-row-wrapper"
    >
      <div
        ref={contentRef}
        style={{
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          borderRadius: 12,
          boxSizing: 'border-box',
          background: 'inherit',
          margin: 0
        }}
      >
        <VoicemailItem
          voicemail={item.voicemail}
          isSelected={item.isSelected}
          onPress={item.onPress}
          onDelete={item.onDelete}
          onRead={item.onRead}
          voicemailSrc={item.voicemailSrc}
          voicemailSrcLoader={item.voicemailSrcLoader}
          useMakeAudioCall={item.useMakeAudioCall}
          useMakeVideoCall={item.useMakeVideoCall}
          itemIndex={item.itemIndex}
          isLocaleGerman={item.isLocaleGerman}
          transcript={item.transcript}
          transcriptKind={item.transcriptKind}
          transcriptLoading={item.transcriptLoading}
          transcriptEnabled={item.transcriptEnabled}
          onRequestTranscript={item.onRequestTranscript}
        />
      </div>
    </div>
  );
});
VoicemailRow.displayName = 'VoicemailRow';

export const VoicemailList: React.FC<IVoicemailListProps> = ({
  items,
  width = '100%',
  itemHeight = 50,
  style = undefined,
  onItemClick,
  isActive,
  testMode = false,
  fetchNextBatch,
}) => {
  const { t } = useTranslation('WebexVoicemail');
  // Track if a fetch is in progress to prevent duplicate calls
  const [isFetching, setIsFetching] = useState(false);
  const prevItemsLength = useRef(items.length);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if user has actually scrolled (to avoid fetches on layout changes)
  const hasUserScrolledRef = useRef<boolean>(false);
  // If we tried fetching and nothing new arrived within timeout, mark exhausted
  const exhaustedRef = useRef<boolean>(false);
  // Track last fetch trigger to avoid duplicates and add a cooldown
  const lastFetchTriggeredLenRef = useRef<number>(0);
  const lastFetchAtRef = useRef<number>(0);
  const fetchStartLengthRef = useRef<number>(0);

  const triggerFetchNextBatch = useCallback(() => {
    if (!fetchNextBatch) {
      return;
    }
    Promise.resolve(fetchNextBatch())
      .catch(() => undefined)
      .finally(() => {
        setIsFetching(false);
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = null;
        }
      });
  }, [fetchNextBatch]);

  // Reset isFetching when new items are received.
  // IMPORTANT: We intentionally do NOT update lastFetchTriggeredLenRef here anymore.
  // Previous behavior set lastFetchTriggeredLenRef to the new length, which meant the
  // inequality check (lastFetchTriggeredLenRef.current !== items.length) inside
  // handleItemsRendered would fail for the next scroll attempt (because both values were equal),
  // blocking subsequent pagination triggers. Instead, we only set lastFetchTriggeredLenRef at the
  // moment we initiate a fetch. That way, after new items arrive and list length increases, the
  // inequality becomes true again, allowing another fetch when the user scrolls near the end.
  useEffect(() => {
    if (items.length > prevItemsLength.current) {
      setIsFetching(false);
      exhaustedRef.current = false; // new data arrived, so we are not exhausted
      // Clear any pending timeout since we got new items
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      // Recompute size for what used to be the last row (now needs gap added)
      if (listRef.current && prevItemsLength.current > 0) {
        const prevLast = prevItemsLength.current - 1;
        listRef.current.resetAfterIndex(prevLast, true);
      }
    }
    // If list length shrank (e.g., refetch or tab reactivation produced fresh data) reset pagination state
    if (items.length < prevItemsLength.current) {
      lastFetchTriggeredLenRef.current = 0;
      exhaustedRef.current = false;
      // If the user has scrolled, avoid forcing scroll to top which is disruptive.
      // Only scroll to top automatically if the user hasn't interacted with the list.
      if (!hasUserScrolledRef.current) {
        try { listRef.current?.scrollTo(0); } catch(_) {}
      }
      if (listRef.current) {
        listRef.current.resetAfterIndex(0, true);
      }
    }
    prevItemsLength.current = items.length;
  }, [items.length]);

  // Safety timeout to reset isFetching if no new items come
  useEffect(() => {
    if (isFetching) {
      fetchTimeoutRef.current = setTimeout(() => {
        // If no new items arrived since this fetch started, consider pagination exhausted
        if (prevItemsLength.current === fetchStartLengthRef.current) {
          exhaustedRef.current = true;
        }
        setIsFetching(false);
        fetchTimeoutRef.current = null;
      }, 5000); // 5 second timeout
    }
    
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [isFetching]);

  // Load first batch on mount
  useEffect(() => {
    if (fetchNextBatch && items.length === 0 && !isFetching) {
      setIsFetching(true);
      fetchStartLengthRef.current = items.length;
      lastFetchAtRef.current = Date.now();
      lastFetchTriggeredLenRef.current = items.length;
      triggerFetchNextBatch();
    }
  }, [fetchNextBatch, items.length, isFetching, triggerFetchNextBatch]);

  // (auto-prefetch effect moved below after dependent declarations)

  // Infinite scroll: fetch next batch when user scrolls near end
  const handleItemsRendered = useCallback(({
    visibleStopIndex
  }: {
    overscanStartIndex: number;
    overscanStopIndex: number;
    visibleStartIndex: number;
    visibleStopIndex: number;
  }) => {
    const now = Date.now();
    const cooldownMs = 1200; // minimal gap between fetch triggers
    const nearEnd = visibleStopIndex >= items.length - 5; // near end

    if (
      !isFetching &&
      !exhaustedRef.current &&
      fetchNextBatch &&
      nearEnd &&
      (lastFetchTriggeredLenRef.current !== items.length || prevItemsLength.current > items.length) &&
      (now - lastFetchAtRef.current) > cooldownMs
    ) {
      setIsFetching(true);
      fetchStartLengthRef.current = items.length;
      lastFetchTriggeredLenRef.current = items.length; // mark the length at fetch trigger
      lastFetchAtRef.current = now;
      triggerFetchNextBatch();
    } else {
      // Verbose debug to understand why we did NOT trigger
      if (nearEnd) {
      }
    }
  }, [fetchNextBatch, items.length, isFetching, triggerFetchNextBatch]);
  const [cssClasses, sc] = useWebexClasses('voicemail-widget');
  const ITEM_HEIGHT = itemHeight;
  const EXPANDED_ITEM_HEIGHT = 300;
  const LOADING_ROW_HEIGHT = 48;
  // Extra breathing room appended to measured expanded row height so that
  // the space BELOW an expanded transcript matches the visual gap ABOVE it.
  // Adjust this value if design spacing changes.
  const EXPANDED_PADDING = 8;
  const listRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);
  // Track the last measured height to avoid redundant state updates
  const lastMeasuredHeightRef = useRef<number | undefined>(undefined);
  // Track whether we've set the parent height once to avoid resize loops
  // Track whether we've set the parent height once to avoid resize loops
  const parentHeightLockedRef = useRef<boolean>(false);
  // hasUserScrolledRef and exhaustedRef declared above

  useEffect(() => {
    if (typeof isActive === 'boolean' && isActive !== true) {
      return;
    }
    // On activation, if no items or very few and not currently fetching, allow immediate prefetch
    if (isActive) {
      hasUserScrolledRef.current = false; // force initial fetch logic to consider empty/short list
      lastFetchTriggeredLenRef.current = -1; // guarantee inequality for next fetch trigger
    }
    let resizeObserver: ResizeObserver | undefined;
    let raf1: number;
    let pendingRaf: number | null = null;
    
    const updateSize = () => {
      const el = containerRef.current;
      if (!el) return;

      // Measure own height and update state only if it changed
      const rect = el.getBoundingClientRect();
      const nextHeight = Math.max(0, Math.floor(rect.height));
      if (nextHeight > 0 && nextHeight !== lastMeasuredHeightRef.current) {
        lastMeasuredHeightRef.current = nextHeight;
        setContainerHeight(nextHeight);
      }

      // Optionally ensure parent height matches grandparent once (prevents flex collapse without loops)
      const parent = el.parentElement;
      const gparentRect = parent?.parentElement?.getBoundingClientRect();
      if (parent && gparentRect && !parentHeightLockedRef.current) {
        const desired = `${Math.floor(gparentRect.height)}px`;
        if (parent.style.height !== desired) {
          parent.style.height = desired;
        }
        parentHeightLockedRef.current = true; // set only once to avoid continuous layout thrashing
      }
    };
    // Double requestAnimationFrame to ensure layout is complete
    raf1 = requestAnimationFrame(() => {
        updateSize();
        if (containerRef.current) {
          resizeObserver = new ResizeObserver(updateSize);
          resizeObserver.observe(containerRef.current);
        }
    });
    const onWindowResize = () => {
      // unlock and re-measure on next frame to adapt to new viewport
      parentHeightLockedRef.current = false;
      if (pendingRaf) cancelAnimationFrame(pendingRaf);
      pendingRaf = requestAnimationFrame(() => {
        updateSize();
        pendingRaf = null;
      });
    };
    window.addEventListener('resize', onWindowResize);

    return () => {
      cancelAnimationFrame(raf1);
      if (pendingRaf) cancelAnimationFrame(pendingRaf);
      if (resizeObserver && containerRef.current) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', onWindowResize);
    };
  }, [isActive]);

  const prevSelectedIndex = useRef<number | null>(null);
  const previousTranscriptEnabled = useRef<boolean[]>([]);
  // Stable reference to latest items array so itemSize fn can read without re-creating closure
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // Lightweight state representing which index is currently visually expanded (affects height)
  const [selectedVisualIndex, setSelectedVisualIndex] = useState<number | null>(null);
  // Dynamic measured heights keyed by voicemail id (only for selected rows)
  const [measuredHeights, setMeasuredHeights] = useState<Record<string, number>>({});

  const onMeasured = useCallback((voicemailId: string, h: number, index: number) => {
    setMeasuredHeights(prev => {
      const existing = prev[voicemailId];
      if (typeof existing === 'number') {
        // Ignore tiny layout jitter (<2px)
        if (Math.abs(existing - h) < 2) return prev;
      }
      const clamped = Math.min(EXPANDED_ITEM_HEIGHT, Math.max(ITEM_HEIGHT, h));
      if (clamped === existing) return prev;
      const next = { ...prev, [voicemailId]: clamped };
      if (listRef.current) {
        listRef.current.resetAfterIndex(index, true);
      }
      return next;
    });
  }, []);
  

  /*
    Stable itemSize function:
    We intentionally depend only on refs and primitive sizing state.
    We DO NOT include the full `items` array so transcript/content changes do not force
    a new function identity (which previously caused react-window to invalidate & remount rows).
    Accessing items[index] at call time still reads latest array contents.
    ESLint exhaustive-deps would normally insist on adding `items`; we suppress on purpose.
  */
  // itemSize function uses refs (itemsRef) plus primitive deps so selection changes propagate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const effectiveItemSizeFn = React.useCallback((index: number) => {
    const currentItems = itemsRef.current;
    if (isFetching && index === currentItems.length) return LOADING_ROW_HEIGHT; // loading row
    const isLast = index === currentItems.length - 1;
    let base = ITEM_HEIGHT;
    const itm = currentItems[index];
    if (itm?.isSelected && itm.transcriptEnabled) {
      const dynamic = measuredHeights[itm.voicemail.id];
      base = typeof dynamic === 'number' ? dynamic : EXPANDED_ITEM_HEIGHT;
    }
    // Gap only represented in the virtual height (no spacer in DOM)
    return isLast ? base : base + EXPANDED_PADDING;
  }, [isFetching, selectedVisualIndex, measuredHeights]);

  // No persistent inflation detection required; rely on CSS overrides and explicit ITEM_HEIGHT

  // Auto-prefetch: If after receiving items we still visually cannot fill viewport (short list)
  // and we're not currently fetching/exhausted, attempt one more fetch. This covers the case
  // where a single page is smaller than the visible window so user cannot scroll to trigger.
  useEffect(() => {
    if (!isFetching && !exhaustedRef.current && fetchNextBatch && containerHeight && items.length > 0) {
      const estimatedTotalHeight = items.reduce(
        (acc, _, idx) => acc + effectiveItemSizeFn(idx),
        0
      );
      if (estimatedTotalHeight < containerHeight - 20) { // small buffer
        // Only trigger if last fetch did increase length (avoid tight loop if API keeps returning empty)
        if (fetchStartLengthRef.current === items.length) return; // last fetch did not grow the list
        setIsFetching(true);
        fetchStartLengthRef.current = items.length;
        lastFetchTriggeredLenRef.current = items.length;
        lastFetchAtRef.current = Date.now();
        triggerFetchNextBatch();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerHeight, effectiveItemSizeFn, items.length, triggerFetchNextBatch]);

  // Build stable itemData object; changes only when its dependencies truly change
  const itemData: RowDataShape = useMemo(() => ({
    items,
    isFetching,
    exhausted: exhaustedRef.current,
    hasUserScrolled: hasUserScrolledRef.current,
    measuredHeights,
    onMeasured,
    t: t as any,
    expandedPadding: EXPANDED_PADDING,
  }), [items, isFetching, t, measuredHeights, onMeasured]);

  // When parent selection state (items[i].isSelected) changes, reset size cache from the earliest changed index
  useEffect(() => {
    if (!listRef.current) return;
    const transcriptEnabled = items.map((item) =>
      Boolean(item.transcriptEnabled)
    );
    const capabilityChangeIndex = transcriptEnabled.findIndex(
      (enabled, index) =>
        enabled !== previousTranscriptEnabled.current[index]
    );
    previousTranscriptEnabled.current = transcriptEnabled;
    if (capabilityChangeIndex >= 0) {
      listRef.current.resetAfterIndex(capabilityChangeIndex, true);
    }
    let currentSelectedIndex: number | null = null;
    for (let i = 0; i < items.length; i += 1) {
      if (items[i]?.isSelected) { currentSelectedIndex = i; break; }
    }
    const prev = prevSelectedIndex.current;
    if (currentSelectedIndex !== prev) {
      const resetIndex = (prev !== null && currentSelectedIndex !== null)
        ? Math.min(prev, currentSelectedIndex)
        : (currentSelectedIndex ?? prev);
      if (resetIndex !== null) {
        // Force update so react-window recalculates immediately with new selectedVisualIndex
        listRef.current.resetAfterIndex(resetIndex, true);
      }
      prevSelectedIndex.current = currentSelectedIndex;
      setSelectedVisualIndex(currentSelectedIndex);
    }
  }, [items]);

  if (testMode) {
    // Render all items directly for testing
    return (
      <VoicemailAudioProvider>
        <div style={{ height: '100%', flex: '1 1 auto', minHeight: 0, ...style }}>
          <Flex direction="column" className={cssClasses} style={{ height: '100%', minHeight: 0 }}>
            {items.map((_, index) => (
              <React.Fragment key={index}>
                {/* Use JSX so React.memo wrapped component is invoked correctly */}
                <VoicemailRow index={index} style={{}} data={itemData} />
              </React.Fragment>
            ))}
          </Flex>
        </div>
      </VoicemailAudioProvider>
    );
  }
  return (
    <VoicemailAudioProvider>
      <div
        ref={containerRef}
        style={{
          height: '100%',
          flex: '1 1 auto',
          minHeight: 0,
          ...style,
        }}
      >
        <Flex
          direction="column"
          className={cssClasses}
          style={{
            height: '100%',
            minHeight: 0,
          }}
        >
          {items && containerHeight && containerHeight > 0 && (
            <div
              role="listbox"
              aria-label="Voicemail list"
              style={{ width: "100%", height: containerHeight }}
            >
              { /* Determine whether to show loading indicator row (only initial empty or after user scroll) */ }
              { (() => { return null; })() }
              <VirtualizedList
                ref={listRef}
                height={containerHeight}
                width={"100%"}
                itemCount={items.length + ((isFetching && !exhaustedRef.current && ((items.length === 0) || hasUserScrolledRef.current)) ? 1 : 0)} // Add loading indicator only on initial empty or after user scroll
                itemSize={effectiveItemSizeFn}
                itemKey={(index) => (isFetching && index === items.length ? 'loading' : (items[index]?.voicemail?.id ?? index))}
                className={sc('list')}
                style={{ width: "100%", height: containerHeight }}
                itemData={itemData}
                onItemsRendered={(args) => {
                  // Only trigger pagination if not marked exhausted and either user scrolled or it's the initial empty state
                  if (!exhaustedRef.current && (hasUserScrolledRef.current || items.length === 0)) {
                    handleItemsRendered(args);
                  }
                }}
                onScroll={() => {
                  // Mark as scrolled on any scroll event (mouse, keyboard, touch, programmatic)
                  hasUserScrolledRef.current = true;
                }}
              >
                {VoicemailRow as any}
              </VirtualizedList>
            </div>
          )}
        </Flex>
      </div>
    </VoicemailAudioProvider>
  );
};
